import { DimText } from "dimtext";
import { dist, midpoint } from "vector";
import { clipImage } from "#src/extensions/location-image.js";
import {
  getNearestScale,
  getScaleLabel,
} from "@local/extensions/utilities/scales.js";
import { transformTilePoint } from "@local/extensions/geometry/transform-sheet-points.js";
import { api } from "#src/api/";
import { readDimension } from "#src/api/ai.js";

const sf = 300 / 72;

function clipPoints(clip) {
  return [
    { x: clip.x, y: clip.y + clip.height },
    { x: clip.x + clip.width, y: clip.y + clip.height },
    { x: clip.x + clip.width, y: clip.y },
    { x: clip.x, y: clip.y },
    { x: clip.x, y: clip.y + clip.height },
  ];
}

function tileSize(w, h, sr) {
  const r = sr % 360;
  if ([90, 270].includes(r)) {
    return { x: h * sf, y: w * sf };
  }

  return { x: w * sf, y: h * sf };
}

function sheetPoint(p, sheetLimits, sheetRotation) {
  const w = sheetLimits[1][0] - sheetLimits[0][0];
  const h = sheetLimits[1][1] - sheetLimits[0][1];
  const ts = tileSize(w, h, sheetRotation);
  const pt = transformTilePoint(p, sheetRotation, sf, ts);

  return {
    x: pt.x + sheetLimits[0][0],
    y: pt.y + sheetLimits[0][1],
  };
}

const dimensionTextAbbrevs = [
  "AFF",
  "AFFL",
  "BOB",
  "BOD",
  "BOF",
  "CC",
  "OC",
  "CL",
  "DIM",
  "EL",
  "ELEV",
  "FF",
  "FFL",
  "MAX",
  "MIN",
  "NTS",
  "RO",
  "REF",
  "TYP",
  "TO",
];

function removeNonDimensionText(text) {
  const dottedSet = new Set(["BOB", "BOD", "BOF", "OC", "FF", "RO"]);

  const patterns = dimensionTextAbbrevs.flatMap((abbrev) => {
    const base = `\\b${abbrev}\\.?(?!\\w)`;
    if (!dottedSet.has(abbrev)) return [base];
    const dotted = `\\b${abbrev.split("").join("\\.")}\\.?(?!\\w)`;
    return [base, dotted];
  });

  const pattern = new RegExp(`(${patterns.join("|")})`, "gi");
  return text.replace(pattern, "").replace(/\s+/g, " ").trim();
}

async function getDimensionScale(grp, doc, page, sheet, pts, clip, orgId) {
  const unit = grp.data.settings?.display_unit ?? "inches";
  const dt = new DimText({ defaultUnit: unit });
  try {
    const limits = sheet.limits;
    const r = sheet.rotation || 0;

    // First check whether there is a text object with the dimension
    const cp = clipPoints(clip).map((p) => sheetPoint(p, limits, r));
    const regions = [cp];

    const overlaps = await api.rpc("find_overlapping_text_objects", {
      document_id: doc.id,
      pages: [page.id],
      regions,
    });

    if (overlaps.error) throw new Error("RPC error: " + overlaps.error);

    const ctr = midpoint(cp[0], cp[2]);

    const dims = overlaps.data[0].overlapping_regions[0]
      .map((r) => {
        let rawText = r.content || "";
        const stripped = removeNonDimensionText(rawText).trim();
        const dim = dt.parse(stripped);
        if (!dim?.value) return null;

        return {
          dim: dim.value,
          dist: dist(ctr, r.insertion_point),
        };
      })
      .filter((d) => d);

    const nearest = dims.sort((a, b) => a.dist - b.dist)[0];
    if (!nearest) throw new Error("No dimension text found.");

    const len = nearest.dim.toNumber("inches");
    const lenIn = dist(pts[0], pts[1]) / 300;
    const sc = lenIn / len;
    const scale = getNearestScale(sc);
    if (!scale) throw new Error("No suitable scale found.");
    return {
      scale,
      label: getScaleLabel(scale),
    };
  } catch (error) {
    // Unable to get scale via text search, will try LLM OCR
    console.log(error);
  }

  try {
    const img = await clipImage(page, clip);
    const res = await readDimension({
      image: img,
      organization_id: orgId,
    });
    if (!res.data.response) throw new Error("No dimension found in response.");
    const dim = dt.parse(res.data.response)?.value;
    if (!dim) throw new Error("Unable to parse.");
    const len = dim.toNumber("inches");
    const lenIn = dist(pts[0], pts[1]) / 300;
    const sc = lenIn / len;
    console.log("getting nearest scale?", sc);
    const scale = getNearestScale(sc);
    console.log(scale);
    if (!scale) throw new Error("No suitable scale found.");
    return {
      scale,
      label: getScaleLabel(scale),
    };
  } catch (error) {
    console.log(error);
    return null;
  }
}

export default getDimensionScale;
